import { Suspense } from 'react';
import type { ComponentType, LazyExoticComponent, ReactElement } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { PanelGuard } from '@favelafood/shared/auth/PanelGuard';
import { lazyWithReload } from '@favelafood/shared/lib/lazyWithReload';
import App from './App';
import { StaffShell } from './shell/StaffShell';

// Área Staff (back-office / onboarding). Base '/', sem prefixo de rota.
const Dashboard = lazyWithReload(() => import('./dashboard/StaffDashboardPage'));
const Pending = lazyWithReload(() => import('./pending/PendingPage'));
const MerchantsList = lazyWithReload(() => import('./merchants/MerchantsListPage'));
const MerchantDetail = lazyWithReload(() => import('./merchant-detail/MerchantDetailPage'));
const ShopsList = lazyWithReload(() => import('./shops/ShopsListPage'));
const ShopDetail = lazyWithReload(() =>
  import('./shop-detail/ShopDetailPage').then((m) => ({ default: m.ShopDetailPage }))
);
const CouriersList = lazyWithReload(() => import('./couriers/CouriersListPage'));
const CourierDetail = lazyWithReload(() => import('./couriers/CourierDetailPage'));
const Settings = lazyWithReload(() =>
  import('./settings/SettingsComingSoon').then((m) => ({ default: m.SettingsComingSoon }))
);
const Novo = lazyWithReload(() => import('./new/NewCadastroHub'));
const NovoComerciante = lazyWithReload(() => import('./new-merchant/NewMerchantWizard'));
const NovoComercio = lazyWithReload(() => import('./new-shop/NewShopWizard'));
const NovoEntregador = lazyWithReload(() => import('./new-courier/NovoEntregadorPage'));

const lazyRoute = (Comp: LazyExoticComponent<ComponentType>): ReactElement => (
  <Suspense fallback={<div className="p-8 text-muted">Carregando...</div>}>
    <Comp />
  </Suspense>
);

/**
 * Browser router for the staff back-office panel (base '/').
 *
 * The whole app is behind a single `PanelGuard(['staff','admin'])` + `StaffShell`
 * (staff is 100% shell). No login route: an unauthenticated visit → PanelGuard →
 * `ensureKeycloakLogin()` → Keycloak. Literals precede the `:id` matchers, as
 * before the carve.
 */
export const routeConfig: RouteObject[] = [
  {
    element: <App />,
    children: [
      {
        element: (
          <PanelGuard allowed={['staff', 'admin']}>
            <StaffShell />
          </PanelGuard>
        ),
        children: [
          { index: true, element: lazyRoute(Dashboard) },
          { path: 'pendentes', element: lazyRoute(Pending) },
          { path: 'comerciantes', element: lazyRoute(MerchantsList) },
          { path: 'comerciantes/:id', element: lazyRoute(MerchantDetail) },
          { path: 'comercios', element: lazyRoute(ShopsList) },
          { path: 'comercios/:id', element: lazyRoute(ShopDetail) },
          { path: 'entregadores', element: lazyRoute(CouriersList) },
          { path: 'entregadores/:id', element: lazyRoute(CourierDetail) },
          { path: 'configuracoes', element: lazyRoute(Settings) },
          // Hub + wizards. Literais antes de qualquer matcher dinâmico.
          { path: 'novo', element: lazyRoute(Novo) },
          { path: 'novo-comerciante', element: lazyRoute(NovoComerciante) },
          { path: 'novo-comercio', element: lazyRoute(NovoComercio) },
          { path: 'novo-entregador', element: lazyRoute(NovoEntregador) },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
];

export const router = createBrowserRouter(routeConfig);
